//
// Copyright (c) 2025 Comcast
//

import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import * as ticketingSystemsSelector from 'selectors/ticketingSystems'
import colors from 'components/utils/colors'

import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import Header from 'components/Shared/Header/Index'
import TicketSidebar from 'components/App/TicketsPage/shared/TicketSidebar'
import PageSection from './PageSection'
import LoadingIndicator from './LoadingIndicator'

const TicketsCount = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.secondary20};
`

const TicketsSearchResultsPage = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const ticketingSystemsList = useSelector(ticketingSystemsSelector.ticketingSystems)
  const [countTicketsMap, setCountTicketsMap] = useState({})
  const [selectedTicket, setSelectedTicket] = useState(null)

  const searchValue = searchParams.get('search') || ''

  useEffect(() => {
    setCountTicketsMap({})
    setSelectedTicket(null)
  }, [searchValue])

  const getBreadcrumbs = () => {
    const breadcrumbsList = [{
      text: t('common.breadcrumbTicketsSearchResults'),
    }]
    return breadcrumbsList
  }

  const onTicketsLoaded = (ticketingSystemDisplayId, countTickets) => {
    setCountTicketsMap(prevState => {
      return { ...prevState, [ticketingSystemDisplayId]: countTickets }
    })
  }

  const onTicketSelected = (ticket) => {
    setSelectedTicket(ticket)
  }

  const onSidebarClose = () => {
    setSelectedTicket(null)
  }

  // get sum of countTicketsMap values
  const getTotalCountTickets = () => {
    return Object.values(countTicketsMap).reduce((acc, item) => acc + item, 0)
  }

  const isLoadingTickets = () => {
    const loadedTicketingSystems = Object.values(countTicketsMap).length
    return (loadedTicketingSystems < ticketingSystemsList.length)
  }

  const totalTicketsCount = getTotalCountTickets()
  const totalTicketsCountText = t('ticketsSearchResultsPage.ticketsFound', { ticketsCount: totalTicketsCount })

  return (
    <MainWrap pageTitle={t('ticketsSearchResultsPage.pageHeader')}>
      <TicketSidebar
        ticket={selectedTicket}
        onClose={onSidebarClose}
        searchValue={searchValue}
      />
      <Content>
        <Header
          title={t('ticketsSearchResultsPage.pageHeader')}
          breadcrumbsList={getBreadcrumbs()}
        />
        <TicketsCount data-test='ticket-search-total-count'>
          {totalTicketsCountText}
          {isLoadingTickets() && <LoadingIndicator />}
        </TicketsCount>

        {ticketingSystemsList.map(ts => {
          return (
            <PageSection
              key={ts.displayId}
              ticketingSystem={ts}
              onTicketsLoaded={onTicketsLoaded}
              onTicketSelected={onTicketSelected}
              selectedTicketId={selectedTicket?.id}
              ticketBonding={ts.ticketBonding}
            />
          )
        })}
      </Content>
    </MainWrap>
  )
}

export default TicketsSearchResultsPage
